/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import styles from './styles.module.scss';
import { Rating } from '@/components/Rating/Rating';
import { roundValueToPrecision } from '@/components/Rating/utils';
import { type ReviewStatistic } from '../../../interface/types';
import { Text, View } from '@az/starc-ui';
import { RatingsReviewsCount } from '../RatingsReviewsCount';
import Hidden from '@/components/Hidden';
import { WriteReviewCTA } from '../WriteReviewCTA/WriteReviewCTA';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type ReviewRatingsAndReviewsProps = {
  reviewStatistic: ReviewStatistic;
  reviewCount?: string;
  recommendedCount?: number;
  isPopup?: boolean;
};

export const ReviewRatingsAndReviews = ({
  reviewStatistic,
  reviewCount,
  recommendedCount,
  isPopup = false,
}: ReviewRatingsAndReviewsProps) => {
  const pdpRedesignEnabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const averageRating = reviewStatistic.averageOverallRating || '0';
  const roundedRating = roundValueToPrecision(Number(averageRating), 0.1);
  const formattedRating = roundedRating.toFixed(1);
  const totalCount = Number(reviewStatistic.totalReviewCount || '0');

  const pdpBottomRedesign = pdpRedesignEnabled && !isPopup;

  return (
    <View direction={'row'} className={styles.container}>
      <View direction={'row'} align={'center'} className={styles.section}>
        <View className={cx(styles.ratingsSection, { [styles['--longGap']]: pdpBottomRedesign })}>
          <Text
            variant="display-3"
            className={cx(styles.average, { [styles['--fontBody']]: pdpBottomRedesign })}
            attributes={{ 'aria-hidden': true }}
          >
            {formattedRating}
          </Text>
          <View className={styles.rating}>
            <Rating
              value={roundedRating}
              showStarFractions
              filledColor="#F26D0D"
              emptyColor="#D6D6D6"
            />
          </View>
        </View>
        {reviewCount ? (
          <RatingsReviewsCount ratingsCount={totalCount} reviewsCount={reviewCount} />
        ) : null}
      </View>

      {isPopup ? null : (
        <Hidden only={'sm'}>
          <WriteReviewCTA
            recommendedCount={recommendedCount ?? 0}
            reviewCount={Number(reviewCount ?? 0)}
            reviewStatistic={reviewStatistic}
          />
        </Hidden>
      )}
    </View>
  );
};
